/**
 * CountingQuestionScreen - PreK Counting Assessment
 *
 * Refactored to use shared hooks and components while preserving
 * all original business logic exactly.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings, DiagnosticAssessmentResult } from './types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../hooks/usePreKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { CountingDots } from '../PreKPractice/Counting/types';

// Shared components and constants
import { AnswerChoicesGrid, AssessmentHeader } from './shared';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import ListenButton from '../common/ListenButton';
import {
  PREK_TRACKS,
  TIMER_CONSTANTS,
  FLUENCY_TARGETS_SEC,
  WARMUP_CONFIG,
  valueToFactId,
  VISUAL_CONFIG,
  TOTAL_ASSESSMENT_QUESTIONS,
  generateAssessmentSequence,
} from './preKConstants';

interface CountingQuestionScreenProps {
  settings: PreKSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

const TRACK_ID = PREK_TRACKS.COUNTING;

const CountingQuestionScreen: React.FC<CountingQuestionScreenProps> = ({
  settings,
  onComplete,
}) => {
  // ============================================================================
  // State Management
  // ============================================================================

  // Question tracking
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  // Warmup state
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);

  // Counting-specific state
  const [currentPattern, setCurrentPattern] = useState<CountingDots | null>(null);
  const [questionPhase, setQuestionPhase] = useState<'ready' | 'question'>('ready');
  const [showDots, setShowDots] = useState(false);

  // Answer state
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  // Timer state
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState<number>(
    TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]
  );
  const [questionTimerActive, setQuestionTimerActive] = useState(false);
  const [timerActive, setTimerActive] = useState(false);

  // TTS state
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);

  // ============================================================================
  // Refs
  // ============================================================================

  const { user, updateUserField } = useAuth();
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);
  const questionSequenceRef = useRef<number[]>([]);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = usePreKTTS();

  // Store generateNextPattern in a ref to avoid dependency issues
  const generateNextPatternRef = useRef<() => CountingDots>(() => ({
    id: 'placeholder',
    quantity: 1,
    dots: [],
  }));

  // ============================================================================
  // Helper Functions
  // ============================================================================

  // Generate dot patterns using 4x8 grid
  const generateCountingDots = useCallback((quantity: number): CountingDots => {
    const shuffledPositions = [...VISUAL_CONFIG.COUNTING_GRID].sort(
      () => Math.random() - 0.5
    );
    const selectedPositions = shuffledPositions.slice(0, quantity);

    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`,
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots,
    };
  }, []);

  // Generate random quantity (1-5 weighted toward 4s and 5s)
  const generateRandomQuantity = useCallback(() => {
    const weightedOptions = [1, 2, 3, 4, 4, 5, 5, 5];
    return weightedOptions[Math.floor(Math.random() * weightedOptions.length)];
  }, []);

  const generateNextPattern = useCallback(() => {
    let newQuantity;
    do {
      newQuantity = generateRandomQuantity();
    } while (currentPattern && newQuantity === currentPattern.quantity);
    return generateCountingDots(newQuantity);
  }, [currentPattern, generateRandomQuantity, generateCountingDots]);

  // Keep ref updated
  generateNextPatternRef.current = generateNextPattern;

  const determineFactStatus = useCallback(
    (isCorrect: boolean, timeSpentMs: number): FactStatus => {
      if (!isCorrect) {
        return 'learning';
      }
      const timeSpentSeconds = timeSpentMs / 1000;
      if (timeSpentSeconds > FLUENCY_TARGETS_SEC[TRACK_ID]) {
        return 'accuracyPractice';
      } else {
        return 'mastered';
      }
    },
    []
  );

  const handleTestComplete = useCallback(
    async (results: DiagnosticAssessmentResult) => {
      if (user?.userId) {
        try {
          await recordPageTransition({
            userId: user.userId,
            trackId: TRACK_ID,
            page: 'other',
          });
        } catch (error) {
          console.error('Error recording final page transition:', error);
        }

        try {
          const result = await completePreKAssessment(user.userId, TRACK_ID, {
            totalQuestions: results.totalQuestions || 0,
            correctAnswers: results.correctAnswers || 0,
            activeTimeElapsed: results.activeTimeElapsed || 0,
          });
          if (result.focusTrack) {
            updateUserField({ focusTrack: result.focusTrack });
          }
        } catch (error) {
          console.error('Failed to save PreK assessment data:', error);
        }
      }
      onComplete(results);
    },
    [user?.userId, onComplete, updateUserField]
  );

  // Generate answer choices (target + both near-misses ±1 + one far miss ±2)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];

    const nearMissPlus = target + 1;
    const nearMissMinus = target - 1;

    if (nearMissPlus >= 0 && nearMissPlus <= 6) {
      choices.push(nearMissPlus);
    }
    if (nearMissMinus >= 0 && nearMissMinus <= 6) {
      choices.push(nearMissMinus);
    }

    const farMissOptions = [target + 2, target - 2];
    const validFarMisses = farMissOptions.filter(
      (option) => option >= 0 && option <= 6 && !choices.includes(option)
    );

    if (validFarMisses.length > 0) {
      const randomFarMiss =
        validFarMisses[Math.floor(Math.random() * validFarMisses.length)];
      choices.push(randomFarMiss);
    }

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 7);
      if (!choices.includes(candidate)) {
        choices.push(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false);
    setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);
    setWrongAnswer(null);
    setShowDots(false);
    setTtsHasPlayed(false);
    setQuestionPhase('ready');
    setHasAnswered(false);
    setSelectedAnswer(null);

    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  const startCountingSequence = useCallback(() => {
    setTtsHasPlayed(false);
    setTimerActive(false);

    if (isFirstQuestionRef.current) {
      setQuestionPhase('ready');
      setTimeout(() => {
        setQuestionPhase('question');
        setShowDots(true);
      }, 500);
    } else {
      // Subsequent questions: skip "Get ready..." phase, show immediately
      setQuestionPhase('question');
      setShowDots(true);
    }
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Initialize first pattern
  useEffect(() => {
    if (!currentPattern) {
      const quantity = generateRandomQuantity();
      setCurrentPattern(generateCountingDots(quantity));
    }
  }, [currentPattern, generateRandomQuantity, generateCountingDots]);

  // Update answer choices when pattern changes
  useEffect(() => {
    if (currentPattern) {
      setAnswerChoices(generateAnswerChoices(currentPattern.quantity));
      setHasAnswered(false);
    }
  }, [currentPattern, generateAnswerChoices]);

  // Set track ID and report page transition on mount
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', TRACK_ID);

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: TRACK_ID,
        page: 'assessment',
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Start sequence when pattern changes
  useEffect(() => {
    if (currentPattern && questionPhase === 'ready') {
      startCountingSequence();
    }
  }, [currentPattern, questionPhase, startCountingSequence]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentPattern]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Question-count completion (replaces wall clock timer)
  useEffect(() => {
    if (!isWarmup && totalQuestions >= TOTAL_ASSESSMENT_QUESTIONS && user?.userId && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      handleTestComplete({
        userId: user.userId,
        testResults: [],
        recommendedStartSkill: 1,
        placementReason: `Completed ${TOTAL_ASSESSMENT_QUESTIONS} questions`,
        completedAt: new Date(),
        totalQuestions,
        correctAnswers,
        activeTimeElapsed,
      });
    }
  }, [totalQuestions, isWarmup, user?.userId, handleTestComplete, correctAnswers, activeTimeElapsed]);

  // Active time tracking
  useEffect(() => {
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed((prev) => prev + TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Play TTS when entering question phase
  useEffect(() => {
    if (questionPhase === 'question' && !ttsHasPlayed) {
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(QUESTION_SCREEN_AUDIO.counting.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        });
      } else {
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [
    questionPhase,
    ttsHasPlayed,
    settings.speechEnabled,
    playTTSWithCallback,
    isWarmup,
    warmupCount,
    currentQuestionIndex,
  ]);

  // Per-question countdown timer
  useEffect(() => {
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (
        questionTimeRemaining <= 0 &&
        questionTimerActive &&
        !isWarmup &&
        !hasAnswered
      ) {
        if (waitingForCorrectAnswer) {
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentPattern(generateCountingDots(questionSequenceRef.current[nextIndex])));
        } else {
          setTotalQuestions((prev) => prev + 1);

          if (user?.userId && currentPattern) {
            try {
              const factId = valueToFactId(TRACK_ID, currentPattern.quantity);
              const factStatus = determineFactStatus(false, TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);

              progressQueue.enqueueProgressUpdate(user.userId, {
                facts: {
                  [factId]: {
                    attempts: 1,
                    correct: 0,
                    timeSpent:
                      questionAccumulatedTimeRef.current +
                      (questionActiveStartRef.current
                        ? Date.now() - questionActiveStartRef.current
                        : 0),
                    status: factStatus,
                  },
                },
              });
            } catch (error) {
              console.error('Error reporting Pre-K counting timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentPattern(generateCountingDots(questionSequenceRef.current[nextIndex])));
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining((prev) => prev - TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    questionTimeRemaining,
    questionTimerActive,
    isWarmup,
    waitingForCorrectAnswer,
    currentQuestionIndex,
    totalQuestions,
    hasAnswered,
    currentPattern,
    user?.userId,
    determineFactStatus,
    resetQuestionState,
    generateCountingDots,
  ]);

  // ============================================================================
  // Warmup Progression
  // ============================================================================

  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= WARMUP_CONFIG.MAX_WARMUP_QUESTIONS) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => setCurrentPattern(generateNextPattern()));
  }, [
    waitingForCorrectAnswer,
    warmupCount,
    generateNextPattern,
    resetQuestionState,
  ]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    const sequence = generateAssessmentSequence(TRACK_ID);
    questionSequenceRef.current = sequence;
    resetQuestionState(() => setCurrentPattern(generateCountingDots(sequence[0])));
  }, [resetQuestionState, generateCountingDots]);

  // ============================================================================
  // Test Progression
  // ============================================================================

  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState(() => setCurrentPattern(generateCountingDots(questionSequenceRef.current[nextIndex])));
      return;
    }

    setCorrectAnswers((prev) => prev + 1);
    setTotalQuestions((prev) => prev + 1);
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
    setCurrentQuestionIndex(nextIndex);
    resetQuestionState(() => setCurrentPattern(generateCountingDots(questionSequenceRef.current[nextIndex])));
  }, [
    waitingForCorrectAnswer,
    currentQuestionIndex,
    resetQuestionState,
    generateCountingDots,
  ]);

  // ============================================================================
  // Answer Handler
  // ============================================================================

  const handleAnswerClick = useCallback(
    (selectedQuantity: number) => {
      if (!currentPattern) return;
      if (waitingForCorrectAnswer && selectedQuantity !== currentPattern.quantity) return;
      if (!waitingForCorrectAnswer && hasAnswered) return;

      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
      setHasAnswered(true);
      setSelectedAnswer(selectedQuantity);

      const isCorrect = selectedQuantity === currentPattern.quantity;

      let timerActiveResponseTime = 0;
      if (!isWarmup) {
        timerActiveResponseTime =
          questionAccumulatedTimeRef.current +
          (questionActiveStartRef.current
            ? Date.now() - questionActiveStartRef.current
            : 0);
      }

      if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
        try {
          const factId = valueToFactId(TRACK_ID, currentPattern.quantity);
          const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting Pre-K counting progress:', error);
        }
      }

      if (isWarmup) {
        if (isCorrect) {
          playChime().then(() => {
            setTimeout(() => {
              advanceWarmupQuestion();
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          });
        } else {
          setWrongAnswer(selectedQuantity);
          setTimeout(() => setWrongAnswer(null), TIMER_CONSTANTS.WRONG_ANSWER_FLASH_MS);

          if (settings.speechEnabled) {
            setTimeout(() => {
              playTTSWithCallback(QUESTION_SCREEN_AUDIO.counting.instruction).catch(
                () => {}
              );
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          }

          setWaitingForCorrectAnswer(true);
          setHasAnswered(false);
        }
      } else {
        // Quiz mode: neutral feedback with chime for all answers, same timing
        if (!waitingForCorrectAnswer && !isCorrect) {
          setTotalQuestions((prev) => prev + 1);
        }

        playChime().then(() => {
          setTimeout(() => {
            if (isCorrect) {
              advanceTestQuestion();
            } else {
              const nextIndex = currentQuestionIndex + 1;
              if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
              setCurrentQuestionIndex(nextIndex);
              resetQuestionState(() => setCurrentPattern(generateCountingDots(questionSequenceRef.current[nextIndex])));
            }
          }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
        });
      }
    },
    [
      currentPattern,
      isTTSPlaying,
      isWarmup,
      user,
      waitingForCorrectAnswer,
      hasAnswered,
      settings.speechEnabled,
      determineFactStatus,
      playChime,
      playTTSWithCallback,
      advanceWarmupQuestion,
      advanceTestQuestion,
      currentQuestionIndex,
      resetQuestionState,
      generateCountingDots,
    ]
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col justify-center">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={false}
        wallClockTimeRemaining={0}
        wallClockStarted={false}
        isTTSPlaying={isTTSPlaying}
      />

      {/* Listen Button - only during question phase */}
      {settings.speechEnabled && questionPhase === 'question' && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => playTTSWithCallback(QUESTION_SCREEN_AUDIO.counting.instruction)}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        {/* Side-by-side layout container */}
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">
          {/* Ready Phase: Waiting - only shown on first question */}
          {questionPhase === 'ready' && isFirstQuestionRef.current && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                Get ready...
              </div>
              <div className="text-lg text-gray-500">Count the objects!</div>
            </div>
          )}

          {/* Question Phase: Show dots and answer choices side by side */}
          {questionPhase === 'question' && (
            <>
              {/* Left Side: Dots Container - 4x8 grid */}
              {showDots && (
                <div
                  className="flex-shrink-0"
                  style={{ width: '200px', height: '400px' }}
                >
                  <div className="relative w-full h-full">
                    {currentPattern.dots.map((dot) => (
                      <motion.div
                        key={dot.id}
                        initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
                        animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
                        transition={isFirstQuestionRef.current ? undefined : { duration: 0.15 }}
                        className="absolute w-6 h-6 lg:w-8 lg:h-8 bg-blue-600 rounded-full shadow-lg"
                        style={{
                          left: `${dot.x}%`,
                          top: `${dot.y}%`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Right Side: Answer Choices - 2x2 grid */}
              <div className="w-full max-w-md lg:max-w-lg">
                <AnswerChoicesGrid
                  choices={answerChoices}
                  correctAnswer={currentPattern.quantity}
                  wrongAnswer={wrongAnswer}
                  hasAnswered={hasAnswered}
                  waitingForCorrectAnswer={waitingForCorrectAnswer}
                  disabled={false}
                  onChoiceClick={handleAnswerClick}
                  selectedAnswer={selectedAnswer}
                  neutralFeedbackMode={!isWarmup}
                  minimalAnimation={!isFirstQuestionRef.current}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountingQuestionScreen;
